import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IDocument } from 'app/models/documents/document';
import { IDocumentType } from 'app/models/documents/documentType';
import { IStatus } from 'app/models/licenses/status';
import { DocumentService } from 'app/services/document/document.service';
import { BlockBlobClient } from "@azure/storage-blob";

@Component({
  selector: 'document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss']
})
export class DocumentComponent implements OnInit {
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() statuses: IStatus[] = [];
  @Input() types: string[] = [];
  @Input() editable: boolean = true;
  @Output() fileUploaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  public documents: IDocument[] = [];
  public documentTypes: IDocumentType[] = [];
  public selectedTypes = new FormControl<IDocumentType[] | null>([]);
  public showTypes: boolean = true;
  public includeArchived: boolean = false;

  public downloadProgress: number = 0;
  public uploadProgress: number = 0;
  public uploading: boolean = false;
  public downloading: boolean = false;
  public sasUrl: string = "";
  public blockBlobClient!: BlockBlobClient;

  public documentColumns: string [] = ['name', 'types', 'dateCreated', 'archived', 'actions'];
  public documentSource = new MatTableDataSource<IDocument>(this.documents);
  @ViewChild('documentPaginator', {static: false}) documentPaginator!: MatPaginator;
  @ViewChild('documentSort', {static: false}) documentSort!: MatSort;
  @ViewChild('fileUpload', {static: false}) fileUpload!: ElementRef<HTMLInputElement>;

  public sharedService = inject(SharedService);
  public documentService = inject(DocumentService);

  ngOnInit(): void {
    this.getDocumentTypes();
    this.getDocuments(this.includeArchived);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['types']){
      this.getDocumentTypes();
    }
  }

  getDocumentTypes(): void {
    this.documentService.getDocumentTypes(this.types).subscribe({
      next: response => this.documentTypes = response,
      error: e => console.log('Error downloading document types', e),
      complete: () => {
        if(this.documentTypes.length === 1){
          this.selectedTypes.setValue(this.documentTypes);
          this.showTypes = false;
        }
      }
    });
  }

  getDocuments(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.documentService.getDocuments(this.entityId, this.entityType, includeArchived).subscribe({
      next: response => this.documents = response,
      error: e => {
        this.sharedService.toastr.error("Error getting documents");
        console.log('Error getting documents', e);
      },
      complete: () => this.updateDocumentTable()
    });
  }

  async generateSASUrl(permission: string, fileName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.documentService.generateSASUrl(permission, fileName).subscribe({
        next: (sas) => this.sasUrl = sas,
        error: (e) => {
          this.sharedService.toastr.error("Could not Get SAS URL for Azure File Storage");
          console.log('Error getting SAS URL', e);
          reject(e);
        },
        complete: () => {
          this.blockBlobClient = new BlockBlobClient(this.sasUrl);
          resolve();
        }
      });
    });
  }

  updateDocumentTable(): void {
    setTimeout(() => {
      this.documentSource.data = this.documents;
      this.documentSource.sort = this.documentSort;
      this.documentSource.paginator = this.documentPaginator;
    }, 500);
  }

  uploadDocument(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) {
      return;
    }
    let dirtyFile = files[0];
    let file = new File([dirtyFile], dirtyFile.name.replace(/[^A-Za-z0-9.]/g, ''));

    if (!this.isValidFileType(dirtyFile)) {
      this.sharedService.openNotice('Invalid file type. Please upload an image or document file.');
      (event.target as HTMLInputElement).value = '';
      return;
    }

    const fileSize = file.size / (1024 * 1024); // Convert to MB
    if (fileSize <= 20) {
      this.uploadToDb(file, event);
    }
    else{
      this.uploadToContainer(file, event);
    }
  }

  uploadToDb(file: File, event:Event): void{
    let types: IDocumentType[] = this.selectedTypes.value || [];

    let upload: IDocument = {
      id: 0,
      name: file.name,
      azureFile: false,
      extension: '',
      parentId: this.entityId,
      adminOnly: false,
      archived: false,
      types: types
    }

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("document", JSON.stringify(upload));
    this.documentService.uploadDocument(formData, this.entityType).subscribe({
      next: response => this.documents.push(response),
      error: e => {
        (event.target as HTMLInputElement).value = '';
        this.sharedService.toastr.error("Could not upload document");
        console.log('Error uploading document', e);
      },
      complete: () => {
        (event.target as HTMLInputElement).value = '';
        this.selectedTypes.setValue([]);
        this.updateDocumentTable();
        this.fileUploaded.emit(true);
      }
    });
  }

  public async uploadToContainer(file: File, event: Event) {
    this.uploadProgress = 0;
    this.uploading = true;

    const blockSize = 4 * 1024 * 1024;
    let offset = 0;
    let counter = 0;
    const blockIds: string[] = [];
    const fileName = this.generateUniqueFileName(file.name);

    const permission = 'create';
    await this.generateSASUrl(permission, fileName);

    try {
      while (offset < file.size) {
        const block = file.slice(offset, offset + blockSize);
        const blockId = btoa(counter.toString().padStart(6, '0'));
        await this.uploadBlock(this.blockBlobClient, blockId, block, progress => {
          this.uploadProgress = Math.round((offset + progress) / file.size * 100);
        });

        blockIds.push(blockId);
        offset += blockSize;
        counter++;
      }
      await this.blockBlobClient.commitBlockList(blockIds);
    }
    catch (error) {
      console.error('Error uploading document to Azure Blob Storage', error);
      this.sharedService.openNotice('Error uploading document. Please try again.');
    }
    finally {
      let types: IDocumentType[] = this.selectedTypes.value || [];
      let upload: IDocument = {
        id: 0,
        name: fileName,
        azureFile: true,
        extension: '',
        parentId: this.entityId,
        adminOnly: false,
        archived: false,
        types: types
      };
      this.uploadAzureDocument(upload);
    }
    (event.target as HTMLInputElement).value = '';
    this.uploadProgress = 0;
    this.uploading = false;
  }

  private uploadAzureDocument(document: IDocument): void {
    this.documentService.uploadAzureDocument(document, this.entityType).subscribe({
      next: response => this.documents.push(response),
      error: e => {
        this.sharedService.toastr.error("Could not upload document to database");
        console.log('Error uploading document to database', e);
      },
      complete: () => {
        this.updateDocumentTable();
        this.fileUploaded.emit(true);
      }
    });
  }

  private async uploadBlock(blockBlobClient: BlockBlobClient,
                            blockId: string,
                            block: Blob, progressCallback?: (progress: number) => void) {
    const response = await blockBlobClient.stageBlock(blockId, block, block.size, {
      onProgress: ({ loadedBytes }) => {
        if (progressCallback) {
          progressCallback(loadedBytes);
        }
      }
    });
    return response;
  }

  generateUniqueFileName(filename: string): string {
    let name = filename.split('.');
    let extension = name.pop();
    let nameWithoutExtension = name.join('.');
    let timestamp = new Date().getTime();
    return nameWithoutExtension + '_' + timestamp + '.' + extension;
  }

  isValidFileType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    return allowedTypes.includes(file.type);
  }

  getReferenceDocument(documentType: string): void {
    this.documentService.getReferenceDocument(documentType).subscribe({
      next: response => this.saveFile(documentType + '.pdf', response),
      error: e => {
        this.sharedService.toastr.error("Could not download reference document");
        console.log('Error downloading reference document', e);
      }
    });
  }

  downloadDocument(document: IDocument): void {
    if (document.azureFile) {
      this.downloadAzureDocument(document.name);
    } else {
      this.downloadDbDocument(document.id, document.name);
    }
  }

  downloadDbDocument(documentId: number, documentName: string): void {
    this.documentService.downloadDocument(documentId).subscribe({
      next: response => this.saveFile(documentName, response),
      error: e => {
        this.sharedService.toastr.error("Could not download document");
        console.log('Error downloading document', e);
      }
    });
  }

  saveFile(fileName: string, blob: Blob) {
    let file = URL.createObjectURL(blob);
    var fileDownload = document.createElement("a");
    fileDownload.href = file;
    fileDownload.download = fileName;
    fileDownload.click();
  }

  async downloadAzureDocument(documentName: string) {
    this.downloadProgress = 0;

    const permission = 'download';
    await this.generateSASUrl(permission, documentName);

    const response = await fetch(this.blockBlobClient.url);
    const totalBytes = parseInt(response.headers.get('Content-Length') || '0', 10);
    let downloadedBytes = 0;
    const chunks: Uint8Array[] = [];

    const readChunk = async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
        const { done, value } = await reader.read();
        if (done) {
            const blob = new Blob(chunks as BlobPart[], { type: 'application/octet-stream' });
            this.saveFile(documentName, blob);
            this.downloading = false;
            return;
        }
        if (value) {
            this.downloading = true;
            chunks.push(value);
            downloadedBytes += value.byteLength;
            this.downloadProgress = Math.round((downloadedBytes / totalBytes) * 100);
        }
        await readChunk(reader);
    };

    const reader = response.body?.getReader();
    if (reader) {
        await readChunk(reader);
    }
  }

  archiveDocument(document: IDocument, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this document?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.documentService.archiveDocument(document.id, archive).subscribe({
            complete: () => {
              document.archived = archive;
              if(archive){
                this.documents = this.documents.filter(d => d.id !== document.id);
              }
              this.updateDocumentTable();
            },
            error: e => {
              this.sharedService.toastr.error('Could not archive document');
              console.log('Error archiving document', e);
            }
          });
        }
      }
    );
  }

  deleteDocument(document: IDocument): void {
    this.sharedService.openConfirm("Delete this document?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          if(document.azureFile) {
            this.deleteAzureDocument(document.id, document.name);
          }
          else {
            this.deleteDbDocument(document.id);
          }
        }
      }
    );
  }

  deleteDbDocument(id: number){
    this.documentService.deleteDocument(id).subscribe({
      complete: () => {
        this.documents = this.documents.filter(d => d.id !== id);
        this.updateDocumentTable();
      },
      error: e => {
        this.sharedService.toastr.error("Could not delete document");
        console.log('Error deleting document', e);
      }
    });
  }

  async deleteAzureDocument(id: number, documentName: string) {
    const permission = 'remove';
    await this.generateSASUrl(permission, documentName);

    try {
      await this.blockBlobClient.delete();
    }
    catch (error) {
      this.sharedService.toastr.error('Could not delete document');
    }
    finally {
      this.deleteDbDocument(id);
      this.documents = this.documents.filter(d => d.id !== id);
      this.updateDocumentTable();
    }
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started);
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }

  public get renewal(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.renewal);
  }

}
